import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { BaseService } from '../../services/base.service';
import { FunnelForm, MockedList } from './tab-funnels-settings-common.interface';
import { Subject, takeUntil } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IFunnel } from '../../interfaces';
import { CrmService } from '../../services/crm.service';
import { NotificationService } from '../../services/notifications.service';
import { Router } from '@angular/router';
import { DialogService } from '../../services/dialog.service';
import * as _ from 'lodash';
import { CURRENCY, activeTranslations } from '../../enum/constants';

@Component({
  selector: 'app-tab-funnels-setting-common',
  templateUrl: './tab-funnels-settings-common.component.html',
  styleUrls: ['./tab-funnels-settings-common.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TabFunnelsSettingsCommonComponent implements OnInit, OnDestroy {
  // Destroy
  private readonly destroy$: Subject<null> = new Subject<null>();

  // Imported const`s, enums and others
  protected list = MockedList;
  protected currency = CURRENCY;

  // Forms relative
  public funnelForm!: FormGroup<FunnelForm>;

  public hasChange: boolean = false;
  public dollar: number = 0;

  // Inputs
  @Input() funnelData!: IFunnel;

  constructor(
    public readonly baseService: BaseService,
    public readonly crmService: CrmService,
    public readonly notificationService: NotificationService,
    public readonly router: Router,
    public readonly dialogService: DialogService,
  ) {}

  ngOnInit() {
    this.initForm();
    this.crmService.getDollar().subscribe({
      next: (res: any) => {
        this.dollar = res;
      },
    });
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private initForm() {
    this.funnelForm = new FormGroup({
      name: new FormControl(this.funnelData?.name, Validators.required),
      currency: new FormControl(this.funnelData?.currency, Validators.required),
    });

    this.onCreateGroupFormValueChange();
  }

  private onCreateGroupFormValueChange() {
    const initialValue: any = { ...this.funnelForm.getRawValue() };
    this.funnelForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      this.hasChange = !_.isEqual(value, initialValue);
    });
  }

  public onSaveFunnel() {
    const funnelName = this.funnelForm.controls.name.value.trim();

    if (funnelName !== '') {
      const payload = this.funnelForm.getRawValue();
      this.crmService
        .updateFunnel(payload, this.funnelData.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: any) => {
            this.funnelData = res;
            this.initForm();
            this.hasChange = false;

            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_saved,
            );
          },
          error: () => {
            this.notificationService.showError(
              '',
              activeTranslations.notifications_texts.error.error_saving,
            );
          },
        });
    } else {
      this.notificationService.showError(
        '',
        activeTranslations.notifications_texts.error.empty_name,
      );
    }
  }

  public onDeleteFunnel() {
    const dialogRef = this.dialogService.openConfirm({
      text: 'dialogs.tab-deals-settings-common',
    });

    dialogRef.subscribe((result) => {
      if (result) {
        this.crmService
          .deleteFunnel(this.funnelData.id)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.notificationService.showSuccess(
                '',
                activeTranslations.notifications_texts.success.successfully_deleted,
              );
              this.router.navigate(['/crm/deals']);
            },
            error: () => {
              this.notificationService.showError(
                '',
                activeTranslations.notifications_texts.error.error_while_delete_1,
              );
            },
          });
      }
    });
  }

  protected readonly CURRENCY = CURRENCY;
}
